import React, { useState, useEffect } from 'react';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, 
  DialogDescription, DialogFooter 
} from '@/ui/dialog';
import { Button } from '@/ui/button';
import { Label } from '@/ui/label';
import { 
  Select, SelectContent,SelectItem, SelectTrigger, SelectValue 
} from '@/ui/select';
import { toast } from 'sonner';
import API from '@/api/api01';
import { BookOpen, User as UserIcon, Activity, CheckCircle2, AlertCircle, Sparkles, ChevronRight, Layers } from 'lucide-react';
import { cn } from '@/ui/utils';
import { Badge } from '@/ui/badge';

interface ImportLMSModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leads: any[]; // Supports single or multiple lead objects
  onImportSuccess: () => void;
}

export default function ImportLMSModal({ open, onOpenChange, leads = [], onImportSuccess }: ImportLMSModalProps) {
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState<any[]>([]);
  const [counselors, setCounselors] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    course_id: '',
    counselor_id: '',
    status: 'New'
  });

  const leadCount = Array.isArray(leads) ? leads.length : (leads ? 1 : 0);
  const leadArray = Array.isArray(leads) ? leads : [leads];

  useEffect(() => {
    if (open) {
      fetchAssignmentOptions();
    }
  }, [open]);

  const fetchAssignmentOptions = async () => {
    try {
      const [coursesRes, usersRes] = await Promise.all([
        API.get('/superadmin/courses'),
        API.get('/superadmin/users')
      ]);
      setCourses(coursesRes.data || []);
      // Filter for admins/staff if possible, or just show all users for now
      setCounselors(usersRes.data || []);
    } catch (err) {
      console.error("Failed to load options", err);
    }
  };

  const handleImport = async () => {
    if (leadCount === 0) return;
    
    // Validation
    if (!formData.course_id) {
       toast.error("Please select a course for assignment");
       return;
    }

    try {
      setLoading(true);
      const leadIds = leadArray.map(l => l.id);
      
      const res = await API.post('/superadmin/social-leads/import-to-lms', {
        lead_ids: leadIds,
        course_id: formData.course_id,
        counselor_id: formData.counselor_id || null,
        status: formData.status
      });

      toast.success(res.data.message || `Successfully imported ${leadCount} leads to LMS`);
      onOpenChange(false);
      
      setTimeout(() => {
        onImportSuccess();
      }, 800);
    } catch (err) {
      toast.error("LMS synchronization failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] h-auto p-0 overflow-hidden bg-background/95 dark:bg-slate-950/95 backdrop-blur-2xl border border-white/10 shadow-[0_50px_100px_rgba(0,0,0,0.4)] rounded-3xl transition-all duration-500">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500" />
        
        <DialogHeader className="p-8 pb-4">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 text-purple-400 text-[10px] font-bold uppercase tracking-wider mb-2 border border-purple-500/20">
              <Sparkles size={12} />
              LMS Bridge Protocol
            </div>
            <DialogTitle className="text-3xl font-black text-slate-900 dark:text-white tracking-tight leading-tight">
              Import to <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400">LMS System</span>
            </DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">
              Transfer leads into your core Learning Management pipeline.
            </DialogDescription>
          </div>
        </DialogHeader>
        
        <div className="p-8 pt-4 space-y-6">
          {/* Lead Summary Card */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl blur opacity-10 group-hover:opacity-20 transition duration-500"></div>
            <div className="relative flex items-center gap-4 p-5 bg-white/5 border border-white/10 rounded-2xl shadow-sm backdrop-blur-xl">
               <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center font-bold text-white shadow-lg">
                  {leadCount > 1 ? <Layers size={24} /> : leadArray[0]?.name?.[0] || <UserIcon size={24} />}
               </div>
               <div className="flex-1">
                  <p className="text-lg font-black text-slate-900 dark:text-white leading-none">
                    {leadCount > 1 ? `${leadCount} Selected Leads` : leadArray[0]?.name || 'Unknown Lead'}
                  </p>
                  <p className="text-xs text-purple-500 font-bold uppercase tracking-widest mt-1.5 flex items-center gap-1">
                    <CheckCircle2 size={12} /> Batch Synchronization Ready
                  </p>
               </div>
               {leadCount > 1 && (
                 <Badge variant="outline" className="rounded-lg bg-white/5 border-white/10 text-[10px]">
                    Batch Mode
                 </Badge>
               )}
            </div>
          </div>

          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-2 ml-1">
                <BookOpen size={14} className="text-purple-500" /> Target Academic Course <span className="text-red-500">*</span>
              </Label>
              {courses.length > 0 ? (
                <Select onValueChange={(v: string) => setFormData({...formData, course_id: v})}>
                  <SelectTrigger className="rounded-2xl h-12 bg-white/5 border-white/10 focus:ring-purple-500 focus:border-purple-500/50 transition-all text-sm">
                    <SelectValue placeholder="Identify relevant course..." />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl border-white/10 bg-slate-900/95 backdrop-blur-2xl p-2">
                    {courses.map(c => (
                      <SelectItem key={c.id} value={c.id} className="rounded-xl focus:bg-purple-500 focus:text-white py-3">
                         <div className="flex flex-col">
                           <span className="font-bold">{c.name}</span>
                           {c.code && <span className="text-[10px] opacity-70">{c.code}</span>}
                         </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex flex-col items-center justify-center p-4 py-6 border border-dashed border-white/10 rounded-2xl bg-white/5">
                   <AlertCircle size={20} className="text-amber-500 mb-2 opacity-50" />
                   <p className="text-[11px] font-bold text-slate-400 uppercase tracking-tighter">No Courses Found</p>
                   <p className="text-[10px] text-slate-500 text-center mt-1">Please initialize courses in the Academic Manager first.</p>
                </div>
              )}
            </div>


            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-2 ml-1">
                  <UserIcon size={14} className="text-indigo-500" /> Counselor
                </Label>
                <Select onValueChange={(v: string) => setFormData({...formData, counselor_id: v})}>
                  <SelectTrigger className="rounded-2xl h-12 bg-white/5 border-white/10 focus:ring-purple-500 transition-all text-sm">
                    <SelectValue placeholder="Assign staff..." />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl border-white/10 bg-slate-900/95 backdrop-blur-2xl p-2">
                    {counselors.map(u => (
                      <SelectItem key={u.id} value={u.id} className="rounded-xl focus:bg-purple-500 focus:text-white py-2">
                         {u.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-2 ml-1">
                  <Activity size={14} className="text-blue-500" /> Initial Status
                </Label>
                <Select defaultValue="New" onValueChange={(v: string) => setFormData({...formData, status: v})}>
                  <SelectTrigger className="rounded-2xl h-12 bg-white/5 border-white/10 focus:ring-purple-500 transition-all text-sm font-bold">
                    <SelectValue placeholder="Set pipeline state" />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl border-white/10 bg-slate-900/95 backdrop-blur-2xl p-2">
                    <SelectItem value="New" className="rounded-xl py-2">New Inquiry</SelectItem>
                    <SelectItem value="Follow-up" className="rounded-xl py-2">In Progress</SelectItem>
                    <SelectItem value="Hot" className="rounded-xl py-2 font-bold text-orange-400">🔥 High Priority</SelectItem>
                    <SelectItem value="Enrolled" className="rounded-xl py-2 font-bold text-emerald-400">✓ Enrolled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <div className="p-4 rounded-2xl bg-amber-500/5 border border-amber-500/10 flex gap-3">
             <AlertCircle size={18} className="text-amber-500 shrink-0 mt-0.5" />
             <p className="text-[11px] text-amber-500/80 leading-relaxed font-medium">
               Importing to LMS will create permanent records in the general leads database. Selected counselors will receive instant notifications.
             </p>
          </div>
        </div>

        <DialogFooter className="p-8 border-t border-white/10 bg-muted/10 backdrop-blur-md flex items-center gap-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-2xl h-12 px-8 font-bold text-slate-500 hover:bg-white/5">
            Cancel
          </Button>
          <Button 
            onClick={handleImport} 
            disabled={loading || leadCount === 0}
            className={cn(
               "flex-1 rounded-2xl h-12 px-10 font-black shadow-xl transition-all duration-300 group overflow-hidden relative",
               "bg-primary hover:bg-primary/90 text-primary-foreground min-w-[200px]"
            )}
          >
            {loading ? (
              <>
                <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                Processing...
              </>
            ) : (
              <div className="flex items-center justify-center gap-2">
                Deploy to LMS System
                <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </div>
            )}
            
            {/* Animated Shine Effect */}
            <div className="absolute top-0 -inset-full h-full w-1/2 z-5 block transform -skew-x-12 bg-gradient-to-r from-transparent to-white/10 opacity-40 group-hover:animate-shine" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
